import { Box, Typography } from "@mui/material";
import Personnummer from "personnummer";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MyTextField } from "../../../components/my-text-field/MyTextField";
import {
  EmailRegExp,
  NationalReserveIdentityRegExp,
  PhoneNumberRegExp,
  ValidPostalRegExp,
  ValidTextRegExp,
  PostalCodeLength,
  TransportationMethod,
} from "../../../Constant";
import MyContext from "../../../contexts/MyContext";
import { FormWrapper } from "../FormWrapper";
import { MyIdentityNumberField } from "../../../components/my-identity-number-field/MyIdentityNumberField";
import { MyCheckbox } from "../../../components/my-checkbox/MyCheckbox";
import ClaimApiService from "../../../adapters/xhr/ClaimApiService";
import { formatDepartureDate, toBase64 } from "../../../Utils";
import { getConfirmation } from "../../../components/my-confirmation-dialog/MyConfirmationDialog";
import { MyTheme } from "../../../infrastructure/MyThemeProvider";
import PostalCodeApiService from "../../../adapters/xhr/PostalCodeApiService";

export const TicketForm2 = (props) => {
  const { t } = useTranslation();
  const myContext = useContext(MyContext);
  const [emailValueErr, setEmailValueErr] = useState("");
  const [firstnameValueErr, setFirstnameValueErr] = useState("");
  const [surnameValueErr, setSurnameValueErr] = useState("");
  const [streetNameAndNumberValueErr, setStreetNameAndNumberValueErr] = useState("");
  const [postalCodeValueErr, setPostalCodeValueErr] = useState("");
  const [cityValueErr, setCityValueErr] = useState("");
  const [personalIDValueErr, setPersonalIDValueErr] = useState("");
  const [mobileNumberValueErr, setMobileNumberValueErr] = useState("");
  const [declareInformationErr, setDeclareInformationErr] = useState("");
  const [bankAccountNumberValueErr, setBankAccountNumberValueErr] = useState("");
  const [clearingNumberValueErr, setClearingNumberValueErr] = useState("");

  const [cityDisabled, setCityDisabled] = useState(false);
  const [validPostalCode, setValidPostalCode] = useState(false);

  const [phoneChanged, setPhoneChanged] = useState(false);
  const [emailChanged, setEmailChanged] = useState(false);

  useEffect(() => {
    if (emailChanged && myContext.email) validateEmail();
    else setEmailValueErr("");
  }, [myContext.email]);

  useEffect(() => {
    if (phoneChanged && myContext.mobileNumber) validatePhone();
    else setMobileNumberValueErr("");
  }, [myContext.mobileNumber]);

  const validateEmail = () => {
    let validEmail = new RegExp(EmailRegExp);
    if (!myContext.email.trim()) {
      setEmailValueErr("email_must_be_specified");
      return false;
    } else if (!validEmail.test(myContext.email.trim())) {
      setEmailValueErr("email_address_is_incorrect");
      return false;
    } else setEmailValueErr("");
    return true;
  };

  const validatePhone = () => {
    let validPhoneNumber = new RegExp(PhoneNumberRegExp);
    if (!myContext.mobileNumber.trim()) {
      setMobileNumberValueErr("mobile_number_must_be_specified");
      return false;
    } else if (!validPhoneNumber.test(myContext.mobileNumber.trim())) {
      setMobileNumberValueErr("entered_invalid_phonenumber");
      return false;
    } else setMobileNumberValueErr("");
    return true;
  };

  const validate = async () => {
    let isValid = true;

    let validNationalReserveIdentity = new RegExp(NationalReserveIdentityRegExp);
    let validPostal = new RegExp(ValidPostalRegExp);

    if (!validateEmail()) isValid = false;

    if (!myContext.bankAccountNumber.trim()) {
      setBankAccountNumberValueErr("bank_account_number_must_be_specified");
      isValid = false;
    } else setBankAccountNumberValueErr(""); 

    if (!myContext.clearingNumber.trim()) {
      setClearingNumberValueErr("clearing_number_must_be_specified");
      isValid = false;
    } else setClearingNumberValueErr("");

    if (!myContext.firstname.trim()) {
      setFirstnameValueErr("first_name_must_be_specified");
      isValid = false;
    } else setFirstnameValueErr("");
    if (!myContext.surname.trim()) {
      setSurnameValueErr("surname_must_be_specified");
      isValid = false;
    } else setSurnameValueErr("");

    if (!myContext.personalID) {
      setPersonalIDValueErr("personal_id_must_be_specified");
      isValid = false;
    } else if (!Personnummer.valid(myContext.personalID) && !validNationalReserveIdentity.test(myContext.personalID.trim())) {
      setPersonalIDValueErr("invalid_person_id");
      isValid = false;
    } else setPersonalIDValueErr("");

    if (!myContext.streetNameAndNumber.trim()) {
      setStreetNameAndNumberValueErr("street_name_and_number_must_be_specified");
      isValid = false;
    } else setStreetNameAndNumberValueErr("");

    if (!myContext.city.trim()) {
      setCityValueErr("city_must_be_specified");
      isValid = false;
    } else setCityValueErr("");

    if (!myContext.postalCode.trim()) {
      setPostalCodeValueErr("postal_code_must_be_specified");
      isValid = false;
    } else setPostalCodeValueErr("");

    if (!validatePhone()) isValid = false;

    if (isValid) await submit();
  };

  const submit = async () => {
    let isValid = true;
    if (!myContext.declareInformation) {
      setDeclareInformationErr("ensure_info");
      isValid = false;
    } else {
      setDeclareInformationErr("");
    }
    if (isValid) {
      let claimReceipts = [];
      
      if (myContext.numberOfKmDriven || myContext.transportTotalCost || (myContext.transportReceipt && myContext.transportReceipt.length > 0)) {
        let transportReceipt = {
          receiptType: "Transportation",
          transportationType: myContext.transportationType,
          totalCost: myContext.transportTotalCost ? Number(myContext.transportTotalCost) : null,
          fromAddress: myContext.fromAddress,
          toAddress: myContext.toAddress,
          distance: myContext.numberOfKmDriven != null ? Number(myContext.numberOfKmDriven) : 0,
          receiptAttachments: await Promise.all(
            myContext.transportReceipt.map(async (receipt) => {
              return {
                file: await toBase64(receipt),
                fileName: receipt.name,
              };
            })
          ),
        };
        claimReceipts.push(transportReceipt);
      }

      let payload = {
        customerInformation: {
          firstName: myContext.firstname.trim(),
          surName: myContext.surname.trim(),
          city: myContext.city?.trim(),
          postalCode: myContext.postalCode?.trim().replace(/\s/g, ""),
          streetNameAndNumber: myContext.streetNameAndNumber?.trim(),
          identityNumber: myContext.personalID?.toUpperCase().trim(),
          mobileNumber: myContext.mobileNumber.trim(),
          email: myContext.email.trim(),
          bankAccountNumber: myContext.bankAccountNumber?.trim(),
          clearingNumber: myContext.clearingNumber?.trim(),
        },
        ticketInformation: {
          ticketNumber: myContext.biljettnummerValue?.toUpperCase().trim(),
          ticketType: myContext.ticketType,
        },
        plannedTravelInformation: {
          transportationMethod: myContext.transportationMethod,
          from: myContext.from?.stopArea?.trim(),
          to: myContext.to?.stopArea?.trim(),
          departureDate: `${formatDepartureDate(myContext.departureDate, myContext.departureTime).toISOString()}`,
          transportationNumber: myContext.line?.number,
        },
        refundType: "BANK_ACCOUNT",
        comment: myContext.otherComments,
        receipts: claimReceipts,
      };

      const result = await ClaimApiService.create(payload);

      if (result?.status !== 200) {
        let message = t("refresh_page_message");
        if (result?.errorMessage == "max_number_of_claims_reached") {
          message = t("max_number_of_claims_reached");
        }
        const confirmed = await getConfirmation({
          message: message,
          theme: MyTheme,
        });
        if (!confirmed) window.location.reload();
      } else {
        myContext.changeCaseNumber(result.data);
        myContext.changeActiveSlide((myContext.activeSlide += 1));
        myContext.resetState();
      }
    }
  };

  return (
    <MyContext.Consumer>
      {(context) => (
        <React.Fragment>
          <FormWrapper continueButtonLabel={t("send_in_application")} onContinue={validate} number={`2/2`} heading={`${t("personal_info")}`}>
            <MyTextField
              required={true}
              autoComplete="given-name"
              value={context.firstname}
              label={t("firstname")}
              userFieldName={true}
              maxLength={35}
              helperText={t(firstnameValueErr)}
              onChange={context.changeFirstname}
              regExp={ValidTextRegExp}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  validate();
                }
              }}
            />
            <MyTextField
              required={true}
              autoComplete="family-name"
              value={context.surname}
              regExp={ValidTextRegExp}
              label={t("surname")}
              userFieldName={true}
              maxLength={35}
              helperText={t(surnameValueErr)}
              onChange={context.changeSurname}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  validate();
                }
              }}
            />
            <MyTextField
              required={true}
              value={context.streetNameAndNumber}
              regExp={ValidTextRegExp}
              label={t("street_name_and_number")}
              userFieldName={true}
              helperText={t(streetNameAndNumberValueErr)}
              onChange={context.changeStreetNameAndNumber}
              maxLength={35}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  validate();
                }
              }}
            />
            <MyTextField
              required={true}
              value={context.postalCode}
              label={t("postal_code")}
              inputMode="numeric"
              regExp={"^[0-9\\s]{0,6}$"}
              maxLength={PostalCodeLength}
              userFieldName={true}
              helperText={t(postalCodeValueErr)}
              onChange={context.changePostalCode}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  validate();
                }
              }}
            />
            <MyTextField
              required={true}
              disabled={cityDisabled}
              value={context.city}
              regExp={ValidTextRegExp}
              label={t("city")}
              userFieldName={true}
              helperText={t(cityValueErr)}
              onChange={context.changeCity}
              maxLength={29}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  validate();
                }
              }}
            />
            <MyIdentityNumberField
              required={true}
              label={t("personal_identifications_number")}
              userFieldName={true}
              helperText={t(personalIDValueErr)}
              value={context.personalID}
              onChange={context.changePersonalID}
              placeholder={t("personal_id_format")}
              inputMode="numeric"
              mask="########-####"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  validate();
                }
              }}
            />
            <MyTextField
              required={true}
              value={context.mobileNumber}
              maxLength={15}
              autoComplete="tel"
              regExp={ValidTextRegExp}
              label={t("mobile_number")}
              type={"tel"}
              userFieldName={true}
              helperText={t(mobileNumberValueErr)}
              onChange={(e) => {
                setPhoneChanged(true);
                context.changeMobileNumber(e);
              }}
              inputMode="numeric"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  validate();
                }
              }}
            />
            <MyTextField
              required={true}
              value={context.email}
              autoComplete="email"
              maxLength={100}
              regExp={ValidTextRegExp}
              label={t("email")}
              userFieldName={true}
              helperText={t(emailValueErr)}
              onChange={(e) => {
                setEmailChanged(true);
                context.changeEmail(e);
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  validate();
                }
              }}
            />
            <MyTextField
              required={true}
              value={context.bankAccountNumber}
              maxLength={16}
              regExp={ValidTextRegExp}
              label={t("bank_account_number")}
              userFieldName={true}
              helperText={t(bankAccountNumberValueErr)}
              onChange={(e) => {
                context.changeBankAccountNumber(e);
              }}
              inputMode="numeric"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  validate();
                }
              }}
            />
            <MyTextField
              required={true}
              value={context.clearingNumber}
              maxLength={10}
              regExp={ValidTextRegExp}
              label={t("clearing_number")}
              userFieldName={true}
              helperText={t(clearingNumberValueErr)}
              onChange={(e) => {
                context.changeClearingNumber(e);
              }}
              className="margin bottom-0"
              inputMode="numeric"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  validate();
                }
              }}
            />
            <MyCheckbox
              label={t("declare_information")}
              helperText={t(declareInformationErr)}
              checked={context.declareInformation}
              onChange={context.changeDeclareInformation}
              className="margin top-1"
              style={{ marginBottom: "0.5rem" }}
            />
            <Typography sx={{ flex: "1 1 100%" }} id="tableTitle" component="p">
              {t("read_about_how_data_processed")}
            </Typography>
          </FormWrapper>
        </React.Fragment>
      )}
    </MyContext.Consumer>
  );
};
